import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { signOut } from "utils/signOut";
import { ADMIN_HOME, currentUserIsStaffInPartnerPortal } from "utils/referralRouting";
import TermsDocument, { termsCaption } from "./TermsDocument";
import useAcceptPartnerTerms from "./useAcceptPartnerTerms";

/** "Kothar Education", or a neutral phrase when the organization has no name on record. */
const organizationLabel = (terms) => terms?.organizationName?.trim() || "this organization";

/**
 * The agreement in force, put in front of everything else until it is accepted. There is no close
 * button and no backdrop dismissal on purpose: the server refuses every other portal call until the
 * partner accepts, so a way past this screen would only lead to pages that cannot load. Signing out
 * (and, for staff, going back to the organization dashboard) stays possible, since neither gets
 * around the terms.
 */
const TermsAcceptanceDialog = ({ terms }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accept = useAcceptPartnerTerms();
  const staff = currentUserIsStaffInPartnerPortal();
  const organization = organizationLabel(terms);
  const current = terms?.current;
  const isUpdate = Boolean(terms?.acceptedVersion);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="partner-terms-title"
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-900/70 p-3 sm:p-6"
    >
      <div className="flex w-full max-w-2xl max-h-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="shrink-0 border-b border-slate-100 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 m-0">
            {organization}
          </p>
          <h2 id="partner-terms-title" className="text-lg font-semibold text-slate-900 m-0 mt-1">
            {isUpdate ? "Updated terms and conditions" : "Terms and conditions"}
          </h2>
          <p className="text-xs text-slate-500 m-0 mt-1">
            {[current?.title, termsCaption(current)].filter(Boolean).join(" · ")}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 bg-slate-50/60">
          <TermsDocument document={current} />
        </div>

        <footer className="shrink-0 border-t border-slate-100 px-5 py-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-900 m-0">
              You must accept these terms and conditions to continue referring clients to{" "}
              <strong>{organization}</strong>.
            </p>
            <p className="text-xs text-amber-800 m-0 mt-1">
              If you continue to use the referral portal, you will be taken to have accepted these
              terms and conditions.
            </p>
          </div>
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3">
              {staff ? (
                <button
                  type="button"
                  onClick={() => navigate(ADMIN_HOME)}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700 min-h-[40px]"
                >
                  Back to organization dashboard
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => signOut({ queryClient, navigate })}
                className="text-xs font-semibold text-slate-500 hover:text-slate-700 min-h-[40px]"
              >
                Sign out
              </button>
            </div>
            <button
              type="button"
              disabled={accept.isLoading}
              onClick={() => accept.mutate()}
              className="rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60 min-h-[44px]"
            >
              {accept.isLoading ? "Recording…" : "Accept and continue"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TermsAcceptanceDialog;
